/**
 * Takes Personify's OAuth return values off the signup entry's address bar.
 *
 * It runs as an inline script in the root layout's `<head>`, before Next's
 * router reads `window.location`: the router records its first URL before any
 * page module loads and writes that URL back on its first commit, so a scrub
 * from a page module would be undone and the code would reach analytics and
 * every later step URL. The function is serialised into that script, so it
 * must stay self-contained: no imports, no outer variables.
 *
 * Only the kind of return is kept, on the root element, for the gate's
 * decision log.
 */
export function scrubPersonifyReturn(): void {
  try {
    // Already scrubbed on this page load: the address is clean and the kind is
    // recorded, so a second pass must not relabel an error as authenticated.
    if (document.documentElement.hasAttribute('data-personify-return')) return;
    const url = new URL(window.location.href);
    // The signup entry is the site root (`SIGNUP_FLOW_ROOT`); a test holds the
    // two equal, since this function cannot import it. The entry's old address
    // forwards to the root with its query, so a return that still lands there
    // is cleaned before the forward carries it on.
    const path = `${url.pathname.replace(/\/+$/, '')}/`;
    const onEntry = path === '/' || path === '/signup/';
    if (!onEntry || url.searchParams.get('client_id') !== '140') return;
    const params = url.searchParams;
    const kind =
      params.has('error') || params.has('error_description')
        ? 'returned_error'
        : params.get('authenticated') === 'true'
          ? 'authenticated'
          : null;
    if (kind === null) return;
    document.documentElement.setAttribute('data-personify-return', kind);
    for (const name of ['code', 'session_state', 'iss', 'error', 'error_description']) {
      params.delete(name);
    }
    params.set('authenticated', 'true');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // An unreadable location leaves nothing to take.
  }
}

/** The `<head>` script body: the function above, called once. */
export const PERSONIFY_RETURN_SCRUB_SCRIPT = `(${scrubPersonifyReturn.toString()})();`;
